import range from 'lodash/range';
import addWeeks from 'date-fns/addWeeks';
import startOfDay from 'date-fns/startOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import subWeeks from 'date-fns/subWeeks';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import messages from '../messages';
import { secondsToHours, type DateRange, type Hours, type Seconds } from '../types';

export const isDateRangeSame = (dateRange1: DateRange, dateRange2: DateRange): boolean =>
	dateRange1.startDateInclusive.getTime() === dateRange2.startDateInclusive.getTime() &&
	dateRange1.endDateExclusive.getTime() === dateRange2.endDateExclusive.getTime();

export const pickDateRange = (obj: DateRange): DateRange => ({
	startDateInclusive: obj.startDateInclusive,
	endDateExclusive: obj.endDateExclusive,
});

// This function is made to mock easily in examples and tests.
export const getCurrentDate = () => new Date();

type TwelveWeeksDateRange = {
	dateRange: DateRange;
	weekStartDates: Date[];
};

export const calculateDateRangeFor12Weeks = (date: Date): TwelveWeeksDateRange => {
	const thisWeekStart = startOfDay(startOfWeek(date, { weekStartsOn: 1 }));
	const elevenWeeksAgo = startOfDay(subWeeks(thisWeekStart, 11));
	return {
		dateRange: {
			startDateInclusive: elevenWeeksAgo,
			endDateExclusive: addWeeks(elevenWeeksAgo, 12),
		},
		weekStartDates: range(12).map((x) => addWeeks(elevenWeeksAgo, x)),
	};
};

export const getWeekEndDateExclusive = (weekStartDateInclusive: Date): Date =>
	addWeeks(weekStartDateInclusive, 1);

type FormatStyle = {
	days: 'long' | 'short';
	hours: 'long' | 'short';
};

const defaultStyle = { days: 'long', hours: 'long' } as const;

const getDaysHoursMessages = (
	roundedTotalHours: Hours,
	style: FormatStyle,
): {
	days: MessageDescriptor;
	hours: MessageDescriptor;
} => {
	const days = {
		long: messages.days,
		short: messages.daysShort,
	}[style.days];
	const hours = {
		long: messages.hours,
		short: messages.hoursShort,
	}[style.hours];

	return { days, hours };
};

export const useFormatDurationFromSeconds = () => {
	const { formatMessage } = useIntl();

	return (totalSeconds: Seconds, style: FormatStyle = defaultStyle) => {
		const hoursFromSeconds = secondsToHours(totalSeconds);
		const roundedTotalHours = Math.round(hoursFromSeconds);
		const days = Math.floor(roundedTotalHours / 24);
		const hours = roundedTotalHours % 24;
		const { days: daysMessage, hours: hoursMessage } = getDaysHoursMessages(
			roundedTotalHours,
			style,
		);
		if (hoursFromSeconds < 1) {
			return formatMessage(messages.lessThanOneHour);
		}
		return `${days > 0 ? formatMessage(daysMessage, { days }) : ''} ${
			hours > 0
				? formatMessage(hoursMessage, {
						hours,
					})
				: ''
		}`.trim();
	};
};
